import React from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';

import { BreadcrumbsV2, DynamicMediaItem } from '@uncinc/react-drupal-core';
import { Container, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';
import type { AddressField, ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { ButtonWrapper, ParagraphsRenderer } from '../../../../components';
import type { TaxonomyLocation } from '../../../../types/uncinc_profile/taxonomy';

import './style.scss';


/**
 * Constructs a link to Google Maps
 * with a search query set to the address
 */
function getLocationHref(address: AddressField) {
  const url = new URL('https://maps.google.com');

  url.searchParams.append(
    'q',
    addressToString(address),
  );

  return url.toString();
}

/**
 * Format address to string.
 * If address line 2 is present,
 * it will be added in parentheses to the end of the string
 */
function addressToString(address: AddressField) {
  let addressString = `${address.address_line1}, ${address.postal_code} ${address.locality}`;

  if (address.address_line2) {
    addressString += ` (${address.address_line2})`;
  }

  return addressString;
}


interface AddressProps {
  address: AddressField;
  name: string;
}

/**
 * Address section for the sidebar
 * If address line 3 is present, it will be rendered as a block of extra information
 */
const Address: React.FC<AddressProps> = ({ address, name }) => (
  <div>
    <h3 className="TaxonomyLocation__sidebar-header">
      {name}
    </h3>
    <address className="TaxonomyLocation__address">
      <ButtonWrapper
        href={getLocationHref(address)}
        className="TaxonomyLocation__address-button"
        type="textual"
        iconName="location_on"
        iconPosition="left"
      >
        {addressToString(address)}
      </ButtonWrapper>
      {address.address_line3 && (
        <div className="TaxonomyLocation__address-extra-info body--small">
          {address.address_line3}
        </div>
      )}
    </address>
  </div>
);

interface TaxonomyLocationProps {
  entity: ImmutableEntity<TaxonomyLocation>;
}

const TaxonomyTaxonomyLocation: React.FC<TaxonomyLocationProps> = ({ entity }) => {
  const classes = c('Taxonomy', 'TaxonomyLocation');

  const { t } = useTranslation('translation', { keyPrefix: 'taxonomies.locations' });

  const locations = entity.toJS();
  const name = locations.name[0].value;
  const description = locations.description[0].value;
  const mediaItem = locations.field_media_item[0];
  const paragraphs = locations.field_paragraphs;
  const email = locations.field_contact_email[0].value;
  const phone = locations.field_contact_phone_number[0].value;
  const contactContent = locations.field_contact_information_text[0]?.value;
  const btw = locations.field_invoice_btw_number[0].value;
  const kvk = locations.field_invoice_kvk_number[0].value;
  const invoiceEmail = locations.field_invoice_email[0].value;
  const postalAddress = locations.field_postal_address[0];
  const visitingAddress = locations.field_address[0];
  const freightAddress = locations.field_delivery_address[0];
  const harborAddress = locations.field_harbor_address[0];

  return (
    <article>
      <Container className={classes}>
        <div className="row">
          <div className="col-12">
            <BreadcrumbsV2 showHomeIcon={false} />
          </div>
        </div>
        <div className="row">
          {/* --- Description --- */}
          <div className="col-12 col-md-8 col-lg-7 order-1">
            <h1>{name}</h1>
            <WysiwygField
              className="body--large"
              html={description}
            />
          </div>
          {/* --- Contact information --- */}
          <div className="col-12 order-3 order-md-2 col-md-4 align-self-end offset-lg-1">
            <section className="TaxonomyLocation__contacts body--small">
              <h2 className="TaxonomyLocation__sidebar-header">
                {t('contact_information')}
              </h2>
              <ButtonWrapper
                href={`mailto:${email}`}
                type="textual"
                iconName="mail"
                iconPosition="left"
              >
                {email}
              </ButtonWrapper>
              <ButtonWrapper
                href={`tel:${phone}`}
                type="textual"
                iconName="call"
                iconPosition="left"
              >
                {phone}
              </ButtonWrapper>
              {visitingAddress && (
                <ButtonWrapper
                  href={getLocationHref(visitingAddress)}
                  type="textual"
                  iconName="location_on"
                  iconPosition="left"
                >
                  {visitingAddress.address_line2 || addressToString(visitingAddress)}
                </ButtonWrapper>
              )}
              {contactContent && (
                <div className="TaxonomyLocation__contacts-extra-info">
                  <WysiwygField
                    className="body--small"
                    html={contactContent}
                  />
                </div>
              )}
            </section>
          </div>
          {/* --- Media --- */}
          {mediaItem && (
            <div className="col-12 order-2 order-md-3">
              <div className="TaxonomyLocation__hero">
                <DynamicMediaItem
                  imageAspectRatio="16_9"
                  media={mediaItem}
                />
              </div>
            </div>
          )}
        </div>
        <div className="row">
          {/* --- Paragraphs --- */}
          <div className="col-12 col-md-7 order-2 order-md-1">
            <section className="TaxonomyLocation__paragraphs-wrapper">
              <ParagraphsRenderer paragraphs={paragraphs} noBootstrap />
            </section>
          </div>
          <div className="col-12 col-md-4 offset-md-1 order-1 order-md-2">
            <div className="TaxonomyLocation__sidebar">
              {/* --- Invoices --- */}
              <section className="Paper TaxonomyLocation__sidebar-content TaxonomyLocation__invoices body--tiny">
                <h2 className="TaxonomyLocation__sidebar-title">
                  {t('invoices')}
                </h2>
                <div>
                  <h3 className="TaxonomyLocation__sidebar-header">
                    {t('btw_nr')}
                  </h3>
                  <div>
                    {btw}
                  </div>
                </div>
                <div>
                  <h3 className="TaxonomyLocation__sidebar-header">
                    {t('kvk_nr')}
                  </h3>
                  <div>
                    {kvk}
                  </div>
                </div>
                <div>
                  <h3 className="TaxonomyLocation__sidebar-header">
                    {t('email')}
                  </h3>
                  <ButtonWrapper
                    href={`mailto:${invoiceEmail}`}
                    type="textual"
                    iconName="mail"
                    iconPosition="left"
                  >
                    {invoiceEmail}
                  </ButtonWrapper>
                </div>
              </section>
              {/* --- Addresses --- */}
              <section className="Paper TaxonomyLocation__sidebar-content TaxonomyLocation__addresses">
                <h2 className="TaxonomyLocation__sidebar-title">
                  {t('addresses')}
                </h2>
                <Address
                  name={t('visiting_address')}
                  address={visitingAddress}
                />
                <Address
                  name={t('postal_address')}
                  address={postalAddress}
                />
                {freightAddress && (
                  <Address
                    name={t('delivery_address')}
                    address={freightAddress}
                  />
                )}
                {harborAddress && (
                  <Address
                    name={t('harbor_address')}
                    address={harborAddress}
                  />
                )}
              </section>
            </div>
          </div>
        </div>
      </Container>
    </article>
  );
};

export default TaxonomyTaxonomyLocation;
